import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  type AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  type ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwSelectModule } from '@cdw-ae/common-ui-components-select';
import { CommonUiComponentsSpinnerComponent } from '@cdw-ae/common-ui-components-spinner';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import { CdwUserOptionComponent } from '@cdw-ae/common-ui-components-user-profile-card';
import { CdwSelectOption } from '@cdw-ae/shared-interfaces';
import { Subject, take, takeUntil } from 'rxjs';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { DeviceSearchByOption, UserSearchByOption } from '../reports.model';
import {
  GenericSearchPanelData,
  GenericSearchResult,
  UserNameMatch,
} from './generic-search-panel.model';

function noSpacesValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').trim();
  return value && /\s/.test(value) ? { noSpaces: true } : null;
}

@Component({
  selector: 'generic-search-panel',
  templateUrl: './generic-search-panel.component.html',
  styleUrls: ['./generic-search-panel.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdwButtonModule,
    CdwSelectModule,
    CdwUserOptionComponent,
    CommonModule,
    CommonUiComponentsSpinnerComponent,
    CommonUiComponentsTextInputModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
})
export class GenericSearchPanelComponent implements OnDestroy, OnInit {
  public searchForm: FormGroup;
  public readonly gray300 = TailwindColors.gray300;
  public readonly gray400 = TailwindColors.gray400;
  public panelTitle: string;
  public searchType: 'user' | 'device';
  public searchByOptions: CdwSelectOption[] = [];
  public searchError?: string;
  public isSearching = false;
  private readonly NAME_SEARCH_RESULTS_LIMIT = 50;

  public currentStep: 1 | 2 = 1;
  public nameSearchResults: UserNameMatch[] = [];

  private readonly destroy$ = new Subject<void>();

  private deviceSearchOptions: CdwSelectOption[] = [
    { value: DeviceSearchByOption.SERIAL_NUMBER, displayName: 'Serial Number' },
    { value: DeviceSearchByOption.DEVICE_ID, displayName: 'CDW Device ID' },
    { value: DeviceSearchByOption.ASSET_ID, displayName: 'Asset ID' },
    {
      value: DeviceSearchByOption.ANNOTATED_USER,
      displayName: 'Annotated User',
    },
  ];

  private userSearchOptions: CdwSelectOption[] = [
    { value: UserSearchByOption.EMAIL, displayName: 'User Email' },
    { value: UserSearchByOption.NAME, displayName: 'Name' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<GenericSearchPanelComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: GenericSearchPanelData
  ) {
    this.panelTitle = data?.title || 'Search';
    this.searchType = data?.searchType || 'device';

    this.searchByOptions =
      this.searchType === 'user'
        ? this.userSearchOptions
        : this.deviceSearchOptions;

    const defaultValue =
      this.searchType === 'user'
        ? UserSearchByOption.EMAIL
        : DeviceSearchByOption.SERIAL_NUMBER;

    this.searchForm = this.formBuilder.group({
      searchBy: [defaultValue, Validators.required],
      searchValue: ['', Validators.required],
      givenName: ['', [noSpacesValidator]],
      familyName: ['', [noSpacesValidator]],
    });

    this.searchForm
      .get('searchValue')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.searchError = undefined;
      });
  }

  ngOnInit(): void {
    this.searchForm
      .get('searchBy')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.searchForm.get('searchValue')?.setValue('', { emitEvent: false });
        this.searchForm.get('givenName')?.setValue('', { emitEvent: false });
        this.searchForm.get('familyName')?.setValue('', { emitEvent: false });
        this.nameSearchResults = [];
        this.searchError = undefined;
        this.currentStep = 1;
        if (this.searchType === 'user') {
          const height = value === UserSearchByOption.NAME ? '450px' : '425px';
          this.dialogRef.updateSize('800px', height);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get searchByLabel(): string {
    return this.searchType === 'user'
      ? 'Identify user by'
      : 'Identify device by';
  }

  public get inputLabel(): string {
    const searchBy = this.searchForm.get('searchBy')?.value;

    switch (searchBy) {
      case DeviceSearchByOption.SERIAL_NUMBER:
        return 'Serial Number';
      case DeviceSearchByOption.DEVICE_ID:
        return 'CDW Device ID';
      case DeviceSearchByOption.ASSET_ID:
        return 'Asset ID';
      case DeviceSearchByOption.ANNOTATED_USER:
        return 'Annotated User';
      case UserSearchByOption.EMAIL:
        return 'User Email';
      default:
        return 'Enter value';
    }
  }

  public readonly inputPlaceholder = 'Exact matches only';

  public trackByNameResult(_index: number, user: UserNameMatch): string {
    return user.email;
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public get isNameSearch(): boolean {
    return this.searchForm.get('searchBy')?.value === UserSearchByOption.NAME;
  }

  public onBack(): void {
    this.currentStep = 1;
    this.nameSearchResults = [];
    this.searchError = undefined;
  }

  public onSelectResult(match: UserNameMatch): void {
    this.data?.onSelectNameMatch?.(match);
    this.dialogRef.close();
  }

  public get resultsAtLimit(): boolean {
    return this.nameSearchResults.length >= this.NAME_SEARCH_RESULTS_LIMIT;
  }

  public async onSearch(): Promise<void> {
    if (this.isSearching) return;
    if (this.isNameSearch) {
      if (this.currentStep === 1 && !this.isSearchDisabled) {
        await this.performNameSearch();
      }
      return;
    }

    this.searchForm.get('searchValue')?.markAsTouched();

    if (!this.searchForm.valid) {
      return;
    }

    this.searchError = undefined;

    const searchValue = this.searchForm.get('searchValue')?.value;

    const trimmedValue = (searchValue ?? '').trim();
    if (!trimmedValue) {
      this.searchError = 'Please enter a valid search term.';
      return;
    }

    this.isSearching = true;

    if (this.data?.onSearch) {
      const error = await this.data.onSearch(
        this.searchForm.value.searchBy,
        trimmedValue
      );

      if (error) {
        this.searchError = error;
        this.isSearching = false;
        this.cdr.markForCheck();
      } else {
        this.isSearching = false;
        this.dialogRef.close();
      }
    } else {
      const result: GenericSearchResult = {
        searchBy: this.searchForm.value.searchBy,
        searchValue: trimmedValue,
      };
      this.isSearching = false;
      this.dialogRef.close(result);
    }
  }

  public get isSearchDisabled(): boolean {
    if (this.isNameSearch) {
      const givenCtrl = this.searchForm.get('givenName');
      const familyCtrl = this.searchForm.get('familyName');
      const given = (givenCtrl?.value || '').trim().length;
      const family = (familyCtrl?.value || '').trim().length;
      const hasErrors =
        givenCtrl?.hasError('noSpaces') || familyCtrl?.hasError('noSpaces');
      return !(given >= 1 || family >= 1) || !!hasErrors || this.isSearching;
    }
    return !this.searchForm.valid || this.isSearching;
  }

  public get showValidationError(): boolean {
    if (this.isNameSearch) return false;
    const searchValueControl = this.searchForm.get('searchValue');
    return !!(
      searchValueControl?.invalid &&
      (searchValueControl?.dirty || searchValueControl?.touched)
    );
  }

  public get validationErrorMessage(): string {
    if (this.isNameSearch) return '';
    const searchValueControl = this.searchForm.get('searchValue');
    if (searchValueControl?.hasError('required')) {
      return `${this.inputLabel} is required`;
    }
    return '';
  }

  private performNameSearch(): Promise<void> {
    return new Promise((resolve) => {
      const givenName = (this.searchForm.get('givenName')?.value || '').trim();
      const familyName = (
        this.searchForm.get('familyName')?.value || ''
      ).trim();

      if (!this.data?.onNameSearch) {
        resolve();
        return;
      }

      this.isSearching = true;
      this.searchError = undefined;
      this.cdr.markForCheck();

      this.data
        .onNameSearch({
          givenName: givenName.length >= 1 ? givenName : undefined,
          familyName: familyName.length >= 1 ? familyName : undefined,
          limit: this.NAME_SEARCH_RESULTS_LIMIT,
        })
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: ({ users }) => {
            this.nameSearchResults = users;
            this.isSearching = false;
            this.currentStep = 2;
            this.cdr.markForCheck();
            resolve();
          },
          error: (err) => {
            this.searchError =
              err.error?.message ||
              'Failed to search for users. Please try again.';
            this.isSearching = false;
            this.cdr.markForCheck();
            resolve();
          },
          complete: () => {
            this.isSearching = false;
            resolve();
          },
        });
    });
  }
}
